// Trading API service for handling market data and trading operations
class TradingApiService {
  constructor() {
    // Use Zonex Trader endpoints
    this.baseURL = import.meta.env?.VITE_ZONEX_API_URL || 'https://manager.zonextrader.com/api';
    this.wsURL = import.meta.env?.VITE_ZONEX_WS_URL || 'wss://general.zonextrader.com/';
    this.ws = null;
    this.subscribers = new Map();
    this.authToken = null;
  }

  // Initialize authentication token
  setAuthToken(token) {
    this.authToken = token;
  }

  // Get stored auth token
  getAuthToken() {
    if (this.authToken) return this.authToken;
    
    // Try to get from localStorage first, then sessionStorage
    const token = localStorage.getItem('zonex_auth_token') || sessionStorage.getItem('zonex_auth_token');
    if (token) {
      this.authToken = token;
    }
    return token;
  }

  // HTTP API Methods
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getAuthToken();
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options?.headers
      },
      ...options
    };

    try {
      const response = await fetch(url, config);
      
      if (!response?.ok) {
        if (response?.status === 401) {
          // Token expired or invalid, clear auth
          this.clearAuth();
        }
        // Instead of throwing error, log and return null for 404 errors
        if (response?.status === 404) {
          console.warn(`Zonex API endpoint not found: ${endpoint}`);
          return null;
        }
        throw new Error(`API Error: ${response.status}`);
      }
      
      return await response?.json();
    } catch (error) {
      // Enhanced error logging but don't throw for network errors
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
        console.warn('Zonex API network error, using fallback data:', error?.message);
        return null;
      }
      console.error('Zonex API request failed:', error);
      return null; // Return null instead of throwing
    }
  }

  // Authentication methods
  async login(email, password, remember = false) {
    const response = await this.makeRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, remember })
    });

    if (response?.token) {
      this.setAuthToken(response?.token);
    }

    return response;
  }

  async register(formData) {
    return this.makeRequest('/auth/register', {
      method: 'POST',
      body: formData // FormData for file uploads
    });
  }

  async logout() {
    try {
      await this.makeRequest('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      this.clearAuth();
    }
  }

  clearAuth() {
    this.authToken = null;
    localStorage.removeItem('zonex_auth_token');
    localStorage.removeItem('zonex_user');
    sessionStorage.removeItem('zonex_auth_token');
    sessionStorage.removeItem('zonex_user');
    this.disconnectWebSocket();
  }

  // Get market symbols with enhanced error handling and fallback
  async getSymbols() {
    try {
      return await this.makeRequest('/symbols');
    } catch (error) {
      console.warn('Symbols API failed, returning fallback data:', error);
      // Return enhanced mock data when API fails
      return [
        { symbol: 'EURUSD', bid: 1.16745, ask: 1.16753, change: 0.0008, changePercent: 0.07, volume: 1250000, category: 'forex' },
        { symbol: 'GBPUSD', bid: 1.34281, ask: 1.34289, change: -0.0012, changePercent: -0.09, volume: 980000, category: 'forex' },
        { symbol: 'XAUUSD', bid: 1935.02, ask: 1935.23, change: 12.45, changePercent: 0.64, volume: 750000, category: 'metals' },
        { symbol: 'XAGUSD', bid: 48.329, ask: 48.342, change: -0.234, changePercent: -0.48, volume: 320000, category: 'metals' },
        { symbol: 'GBPJPY', bid: 201.887, ask: 201.903, change: 0.456, changePercent: 0.23, volume: 560000, category: 'forex' },
        { symbol: 'GER30', bid: 23823.8, ask: 23825.4, change: -45.2, changePercent: -0.28, volume: 420000, category: 'indices' },
        { symbol: 'US500', bid: 6135.6, ask: 6136.2, change: 23.4, changePercent: 0.57, volume: 890000, category: 'indices' },
        { symbol: 'USDJPY', bid: 151.234, ask: 151.247, change: 0.456, changePercent: 0.30, volume: 456789, category: 'forex' },
        { symbol: 'AUDUSD', bid: 0.65432, ask: 0.65438, change: -0.0015, changePercent: -0.23, volume: 345670, category: 'forex' },
        { symbol: 'BTCUSD', bid: 43250.00, ask: 43275.00, change: 1250.00, changePercent: 2.98, volume: 125000, category: 'crypto' },
        { symbol: 'ETHUSD', bid: 2485.75, ask: 2487.25, change: 85.50, changePercent: 3.56, volume: 89000, category: 'crypto' }
      ];
    }
  }

  // Get symbol details
  async getSymbolDetails(symbol) {
    return this.makeRequest(`/symbols/${symbol}`);
  }

  // Get historical OHLC data
  async getOHLCData(symbol, timeframe = '1H', limit = 100) {
    return this.makeRequest(`/ohlc/${symbol}?timeframe=${timeframe}&limit=${limit}`);
  }

  // Place trading order
  async placeOrder(orderData) {
    return this.makeRequest('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData)
    });
  }

  // Get open positions with enhanced error handling
  async getPositions() {
    try {
      const result = await this.makeRequest('/positions');
      return result || []; // Return empty array if API fails
    } catch (error) {
      console.warn('Positions API failed, returning fallback data:', error);
      return [];
    }
  }

  // Get pending orders with enhanced error handling
  async getPendingOrders() {
    try {
      const result = await this.makeRequest('/orders/pending');
      return result || []; // Return empty array if API fails
    } catch (error) {
      console.warn('Pending orders API failed, returning fallback data:', error);
      return [];
    }
  }

  // Get trade history with enhanced error handling
  async getTradeHistory(limit = 100) {
    try {
      const result = await this.makeRequest(`/orders/history?limit=${limit}`);
      return result || []; // Return empty array if API fails
    } catch (error) {
      console.warn('Trade history API failed, returning fallback data:', error);
      return [];
    }
  }

  // Close position
  async closePosition(positionId) {
    return this.makeRequest(`/positions/${positionId}/close`, {
      method: 'POST'
    });
  }

  // Cancel order
  async cancelOrder(orderId) {
    return this.makeRequest(`/orders/${orderId}`, {
      method: 'DELETE'
    });
  }

  // WebSocket Methods
  connectWebSocket(onMessage, onError, onClose) {
    if (this.ws && this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      this.ws = new WebSocket(this.wsURL);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected to Zonex Trader');
        
        // Authenticate WebSocket connection
        const token = this.getAuthToken();
        if (token) {
          this.ws?.send(JSON.stringify({
            action: 'authenticate',
            token: token
          }));
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event?.data);
          
          // Validate message structure
          if (!data || typeof data !== 'object') {
            console.warn('Received invalid WebSocket message structure:', data);
            return;
          }

          // Handle different message types
          switch (data?.type) {
            case 'price_update':
              this.handlePriceUpdate(data);
              break;
            case 'ohlc_update': case'chart':
              this.handleOHLCUpdate(data);
              break;
            case 'order_update':
              this.handleOrderUpdate(data);
              break;
            case 'position_update':
              this.handlePositionUpdate(data);
              break;
            case 'market_status':
              this.handleMarketStatusUpdate(data);
              break;
            case 'news_update':
              this.handleNewsUpdate(data);
              break;
            case 'authentication': console.log('WebSocket authenticated successfully');
              break;
            case 'error': console.error('WebSocket error from server:', data?.message);
              break;
            default:
              // Only log if type is explicitly undefined or null
              if (data?.type === undefined || data?.type === null) {
                console.warn('Received WebSocket message with undefined/null type:', data);
              } else {
                console.log('Unknown Zonex WebSocket message type:', data?.type);
              }
          }
          
          if (onMessage) {
            onMessage(data);
          }
        } catch (error) {
          console.error('Error parsing Zonex WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('Zonex WebSocket error:', error);
        if (onError) {
          onError(error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('Zonex WebSocket disconnected:', event?.code, event?.reason);
        if (onClose) {
          onClose(event);
        }
        
        // Attempt to reconnect after 5 seconds if we have auth token
        if (this.getAuthToken()) {
          setTimeout(() => {
            this.connectWebSocket(onMessage, onError, onClose);
          }, 5000);
        }
      };
      
    } catch (error) {
      console.error('Failed to connect to Zonex WebSocket:', error);
      if (onError) {
        onError(error);
      }
    }
  }

  // Subscribe to symbol price updates
  subscribeToPrices(symbols) {
    if (this.ws && this.ws?.readyState === WebSocket.OPEN) {
      this.ws?.send(JSON.stringify({
        action: 'subscribe_prices',
        symbols: Array.isArray(symbols) ? symbols : [symbols]
      }));
    }
  }

  // Subscribe to OHLC data for charting
  subscribeToOHLC(symbol, timeframe = '1M') {
    if (this.ws && this.ws?.readyState === WebSocket.OPEN) {
      this.ws?.send(JSON.stringify({
        action: 'subscribe_ohlc',
        symbol,
        timeframe
      }));
    }
  }

  // Subscribe to news updates
  subscribeToNews() {
    if (this.ws && this.ws?.readyState === WebSocket.OPEN) {
      this.ws?.send(JSON.stringify({
        action: 'subscribe_news'
      }));
    }
  }

  // Subscribe to market status updates
  subscribeToMarketStatus() {
    if (this.ws && this.ws?.readyState === WebSocket.OPEN) {
      this.ws?.send(JSON.stringify({
        action: 'subscribe_market_status'
      }));
    }
  }

  // Unsubscribe from updates
  unsubscribe(symbol) {
    if (this.ws && this.ws?.readyState === WebSocket.OPEN) {
      this.ws?.send(JSON.stringify({
        action: 'unsubscribe',
        symbol
      }));
    }
  }

  // Handle price updates
  handlePriceUpdate(data) {
    const subscribers = this.subscribers?.get('price_' + data?.symbol);
    if (subscribers) {
      subscribers?.forEach(callback => callback(data));
    }
  }

  // Handle OHLC updates for charts
  handleOHLCUpdate(data) {
    const subscribers = this.subscribers?.get('ohlc_' + data?.symbol);
    if (subscribers) {
      subscribers?.forEach(callback => callback(data));
    }
  }

  // Handle order updates
  handleOrderUpdate(data) {
    const subscribers = this.subscribers?.get('orders');
    if (subscribers) {
      subscribers?.forEach(callback => callback(data));
    }
  }

  // Handle position updates
  handlePositionUpdate(data) {
    const subscribers = this.subscribers?.get('positions');
    if (subscribers) {
      subscribers?.forEach(callback => callback(data));
    }
  }

  // Handle market status updates
  handleMarketStatusUpdate(data) {
    const subscribers = this.subscribers?.get('market_status');
    if (subscribers) {
      subscribers?.forEach(callback => callback(data));
    }
  }

  // Handle news updates
  handleNewsUpdate(data) {
    const subscribers = this.subscribers?.get('news');
    if (subscribers) {
      subscribers?.forEach(callback => callback(data));
    }
  }

  // Subscribe to specific data updates
  subscribe(type, symbol, callback) {
    const key = symbol ? `${type}_${symbol}` : type;
    
    if (!this.subscribers?.has(key)) {
      this.subscribers?.set(key, new Set());
    }
    
    this.subscribers?.get(key)?.add(callback);
    
    // Return unsubscribe function
    return () => {
      const subscribers = this.subscribers?.get(key);
      if (subscribers) {
        subscribers?.delete(callback);
        if (subscribers?.size === 0) {
          this.subscribers?.delete(key);
        }
      }
    };
  }

  // Disconnect WebSocket
  disconnectWebSocket() {
    if (this.ws) {
      this.ws?.close();
      this.ws = null;
    }
    this.subscribers?.clear();
  }

  // Mock data generators for development/testing
  generateMockPriceData(symbol, basePrice) {
    const variation = (Math.random() - 0.5) * 0.001;
    const bid = basePrice + variation;
    const ask = bid + (Math.random() * 0.0005);
    
    return {
      symbol,
      bid: parseFloat(bid?.toFixed(5)),
      ask: parseFloat(ask?.toFixed(5)),
      timestamp: Date.now(),
      change: variation,
      changePercent: (variation / basePrice) * 100
    };
  }

  generateMockOHLCData(symbol, timeframe = '1M', count = 100) {
    const data = [];
    let currentPrice = Math.random() * 2 + 1; // Random price between 1-3
    const now = Date.now();
    const intervalMs = this.getTimeframeMs(timeframe);
    
    for (let i = count; i >= 0; i--) {
      const time = now - (i * intervalMs);
      const variation = (Math.random() - 0.5) * 0.01;
      const open = currentPrice;
      const close = open + variation;
      const high = Math.max(open, close) + Math.random() * 0.005;
      const low = Math.min(open, close) - Math.random() * 0.005;
      
      data?.push({
        symbol,
        timeframe,
        timestamp: time,
        open: parseFloat(open?.toFixed(5)),
        high: parseFloat(high?.toFixed(5)),
        low: parseFloat(low?.toFixed(5)),
        close: parseFloat(close?.toFixed(5)),
        volume: Math.floor(Math.random() * 10000) + 1000
      });
      
      currentPrice = close;
    }
    
    return data;
  }

  getTimeframeMs(timeframe) {
    const timeframes = {
      '1M': 60000,
      '5M': 300000,
      '15M': 900000,
      '30M': 1800000,
      '1H': 3600000,
      '4H': 14400000,
      '1D': 86400000,
      '1W': 604800000
    };
    return timeframes?.[timeframe] || 60000;
  }

  // Get connection status
  getConnectionStatus() {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws?.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
      case WebSocket.CLOSED:
      default:
        return 'disconnected';
    }
  }
}

// Export singleton instance
export const tradingApi = new TradingApiService();
export default tradingApi;